import React, { useMemo, useState } from 'react'
import { useField } from 'formik'
import ChipInput from 'material-ui-chip-input'
import * as Yup from 'yup'
import makeStyles from '@material-ui/core/styles/makeStyles'
import { ErrorMessage } from 'components/Shared/ErrorMessage'
import { useValidationSchema } from 'context/validationSchema'
import { isFieldRequired } from 'components/Shared/FormikForm'
import { PrimaryChip } from 'components/Shared/PrimaryChip'

const invalidEmailMessage = 'form.order.email'
const requiredMessage = 'form.validation.required'
const emailSchema = Yup.string().email(invalidEmailMessage)
const requiredSchema = Yup.string().required(requiredMessage)

const useStyles = makeStyles({
  root: {
    marginBottom: 20,
    textAlign: 'left',
    maxWidth: 308
  },
  chipContainer: {
    display: 'block',
    maxWidth: '310px',
    backgroundColor: '#FFF',
    border: '1px solid #e2e2e1',
    borderRadius: '3px',
    padding: '10px',
    minHeight: 58,
    minWidth: 310,
  },
  helperText: {
    '& span': {
      bottom: '-42px',
      left: '11px'
    }
  },
  input: {
    padding: '-10px'
  },
  disabledChipContainer: {
    cursor: 'auto'
  }
})

function MailChip ({ text, disabled, onDelete, onEdit }) {
  return (
    <PrimaryChip
      onClick={() => onEdit()}
      onDelete={onDelete}
      label={text}
      disabled={disabled}
    />
  )
}

export function MailField ({ name, placeholder, disabled }) {
  const classes = useStyles()

  const [inputValue, setInputValue] = useState('')

  const [, meta, helpers] = useField(name)
  const { value, touched } = meta
  const { setValue, setTouched, setError } = helpers
  const { schema } = useValidationSchema()

  const isRequired = useMemo(() => isFieldRequired(schema, name), [schema, name])

  function dropEmail (index) {
    const newValue = value.slice(0, index).concat(value.slice(index + 1))
    setValue(newValue)
    if (!requiredSchema.isValidSync(newValue))
      setError(requiredMessage)
  }

  function handleAdd (email) {
    const newValue = [...value, email]
    setValue(newValue)
    setError(null)
    setInputValue('')
  }

  function handleDelete (email, index) {
    dropEmail(index)
  }

  function handleAddTry (email) {
    const isValid = emailSchema.isValidSync(email)
    if (!isValid) {
      setError(invalidEmailMessage)
    }
    return isValid
  }

  function handleEditClick (index) {
    const email = value[index]
    dropEmail(index)
    setInputValue(email)
  }

  function handleInputUpdate (event) {
    // check to prevent form rerender
    if (!touched) {
      setTouched(true)
    }
    setInputValue(event.target.value)
  }

  return <ChipInput
    helperText={<ErrorMessage name={name}/>}
    inputValue={inputValue}
    disabled={disabled}
    chipRenderer={({ value, text, isFocused, isDisabled, handleClick, handleDelete, className }, key) =>
      (
        <MailChip
          key={key}
          text={text}
          onEdit={() => handleEditClick(key)}
          onDelete={handleDelete}
          disabled={disabled}
        />
      )}
    value={value}
    onBeforeAdd={handleAddTry}
    classes={{
      root: classes.root,
      chipContainer: classes.chipContainer,
      helperText: classes.helperText,
      input: classes.input,
      disabled: classes.disabledChipContainer
    }}
    onAdd={handleAdd}
    onDelete={handleDelete}
    onUpdateInput={handleInputUpdate}
    placeholder={placeholder + (isRequired ? ' *' : '')}
    alwaysShowPlaceholder
    disableUnderline
    fullWidthInput
  />
}
